import React, { useState } from 'react';
import { ApplicationData } from '../LoanApplicationFlow';
import { formatRand, quoteLoan } from '../../../utils/loanPricing';
import '../StepStyles.css';

interface Step1AmountProps {
  data: ApplicationData;
  onUpdate: (data: Partial<ApplicationData>) => void;
  onNext: () => void;
}

const Step1Amount: React.FC<Step1AmountProps> = ({ data, onUpdate, onNext }) => {
  const [amount, setAmount] = useState(data.loanAmount);
  
  // Business rules — must match LoanCalculator.tsx constants
  const MIN_AMOUNT = 200;
  const MAX_AMOUNT = 750;

  const loanDetails = quoteLoan(amount);

  // Handle amount change
  const handleAmountChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newAmount = parseInt(event.target.value);
    setAmount(newAmount);
    onUpdate({ loanAmount: newAmount });
  };

  // Handle direct input change
  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value.replace(/[^\d]/g, '');
    const newAmount = Math.max(MIN_AMOUNT, Math.min(MAX_AMOUNT, parseInt(value) || MIN_AMOUNT));
    setAmount(newAmount);
    onUpdate({ loanAmount: newAmount });
  };

  // Quick amount buttons
  const quickAmounts = [200, 350, 500, 750];

  // Handle continue
  const handleContinue = () => {
    onUpdate({ loanAmount: amount });
    onNext();
  };

  return (
    <div className="step-container">
      <div className="step-header">
        <h2 className="step-title">Confirm Your Loan Amount</h2>
        <p className="step-description">
          Adjust your loan amount if needed. You can borrow between R200 and R750.
        </p>
      </div>

      <div className="step-content">
        {/* Amount Selector */}
        <div className="amount-selector-section">
          <div className="amount-input-container">
            <label htmlFor="loan-amount" className="amount-label">
              Loan Amount
            </label>
            
            {/* Direct input */}
            <div className="amount-input-wrapper">
              <span className="currency-symbol">R</span>
              <input
                type="text"
                id="loan-amount-input"
                className="amount-input"
                value={amount}
                onChange={handleInputChange}
                placeholder="400"
              />
            </div>

            {/* Slider */}
            <div className="slider-container">
              <input
                type="range"
                id="loan-amount"
                className="amount-slider"
                min={MIN_AMOUNT}
                max={MAX_AMOUNT}
                step="50"
                value={amount}
                onChange={handleAmountChange}
              />
              <div className="slider-labels">
                <span>R{MIN_AMOUNT}</span>
                <span>R{MAX_AMOUNT}</span>
              </div>
            </div>

            {/* Quick amount buttons */}
            <div className="quick-amounts">
              {quickAmounts.map((quickAmount) => (
                <button
                  key={quickAmount}
                  className={`quick-amount-btn ${amount === quickAmount ? 'active' : ''}`}
                  onClick={() => {
                    setAmount(quickAmount);
                    onUpdate({ loanAmount: quickAmount });
                  }}
                  type="button"
                >
                  R{quickAmount}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Loan Summary */}
        <div className="modern-loan-summary">
          <div className="loan-summary-header">
            <div className="loan-summary-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="12" y1="1" x2="12" y2="23"/>
                <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
              </svg>
            </div>
            <div className="loan-summary-title">
              <h3>Loan Summary</h3>
              <p>See your loan breakdown and repayment details</p>
            </div>
          </div>
          
          <div className="loan-calculation-card">
            <div className="calculation-row">
              <span className="calc-label">You Get</span>
              <span className="calc-value primary">{formatRand(amount)}</span>
            </div>

            <div className="calculation-divider"></div>

            <div className="calculation-row total">
              <span className="calc-label">You repay</span>
              <span className="calc-value">{formatRand(loanDetails.totalAmount)}</span>
            </div>

            <div className="repayment-note">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M9 12l2 2 4-4"/>
                <path d="M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9s4.03-9 9-9c1.51 0 2.93.37 4.18 1.03"/>
              </svg>
              <span>Interest {formatRand(loanDetails.interestAmount)}, admin {formatRand(loanDetails.adminFeeExclVat)}, VAT {formatRand(loanDetails.vatAmount)}, initiation {formatRand(loanDetails.initiationFee)}. <a href="/faq" style={{ color: '#0074D9', textDecoration: 'none', fontWeight: 600 }}>Full breakdown</a></span>
            </div>
            
            <div className="repayment-date-card">
              <div className="repayment-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                  <line x1="16" y1="2" x2="16" y2="6"/>
                  <line x1="8" y1="2" x2="8" y2="6"/>
                  <line x1="3" y1="10" x2="21" y2="10"/>
                </svg>
              </div>
              <div className="repayment-info">
                <span className="repayment-label">Repay by</span>
                <span className="repayment-date">{loanDetails.repaymentDateLabel}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Important Information */}
        <div className="modern-info-section">
          <div className="info-header">
            <div className="info-header-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"/>
                <path d="M12 16v-4"/>
                <path d="M12 8h.01"/>
              </svg>
            </div>
            <div className="info-header-text">
              <h4>Important Information</h4>
              <p>Key details about your loan process</p>
            </div>
          </div>
          
          <div className="info-items">
            <div className="info-item">
              <div className="info-item-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/>
                  <polyline points="12,6 12,12 16,14"/>
                </svg>
              </div>
              <div className="info-item-content">
                <span className="info-item-title">Processing Time</span>
                <span className="info-item-description">Application review and approval within 24 hours</span>
              </div>
            </div>
            
            <div className="info-item">
              <div className="info-item-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 2l10 5-10 5L2 7l10-5z"/>
                  <path d="M2 17l10 5 10-5"/>
                  <path d="M2 12l10 5 10-5"/>
                </svg>
              </div>
              <div className="info-item-content">
                <span className="info-item-title">Quick Transfer</span>
                <span className="info-item-description">Once approved, funds transferred to your account within hours</span>
              </div>
            </div>
            
            <div className="info-item">
              <div className="info-item-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                  <line x1="16" y1="2" x2="16" y2="6"/>
                  <line x1="8" y1="2" x2="8" y2="6"/>
                  <line x1="3" y1="10" x2="21" y2="10"/>
                </svg>
              </div>
              <div className="info-item-content">
                <span className="info-item-title">Automatic Repayment</span>
                <span className="info-item-description">Repayment is automatically deducted on your NSFAS payment date</span>
              </div>
            </div>
            
            <div className="info-item">
              <div className="info-item-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M9 12l2 2 4-4"/>
                  <path d="M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9s4.03-9 9-9c1.51 0 2.93.37 4.18 1.03"/>
                </svg>
              </div>
              <div className="info-item-content">
                <span className="info-item-title">Transparent Pricing</span>
                <span className="info-item-description">No hidden fees - what you see is what you pay</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Step Actions */}
      <div className="step-actions">
        <button
          className="continue-btn primary"
          onClick={handleContinue}
          type="button"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M5 12h14"/>
            <path d="M12 5l7 7-7 7"/>
          </svg>
          Continue
        </button>
        
        <p className="step-note">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M9 12l2 2 4-4"/>
            <path d="M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9s4.03-9 9-9c1.51 0 2.93.37 4.18 1.03"/>
          </svg>
          Secure application • R{amount} loan
        </p>
      </div>
    </div>
  );
};

export default Step1Amount;

